import { apiClient } from '@/utils/api-client';
import type { PageResult } from '@/types/drawdown';
import type { TransactionActivityEntry } from '@/types/transaction-activity';
import type {
  AddBankEntryRequest,
  ApiBankEntryResponse,
  ApiFundTransactionDetail,
  ApiFundTransactionSummary,
  BankEntryResponse,
  FundTransactionCategory,
  FundTransactionDetail,
  FundTransactionDocument,
  FundTransactionDocumentCreateResponse,
  FundTransactionDocumentLinkRequest,
  FundTransactionGLTransaction,
  FundTransactionStatus,
  FundTransactionSummary,
  FundTransactionType,
  GenerateManagementFeeDraftsResponse,
  ManualApproveRequest,
  ReconciliationStateResponse,
  UpdateBankEntryRequest,
} from '@/types/fund-transactions';
import {
  mapBankEntryResponse,
  mapFundTransactionDetail,
  mapFundTransactionSummary,
} from './fundTransactionMapper';

function _qs(
  params: Record<string, string | number | boolean | null | undefined>
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const fundTransactionService = {
  async list(
    fundId: string,
    params: {
      transaction_type?: FundTransactionType;
      transaction_category?: FundTransactionCategory;
      status?: FundTransactionStatus;
      date_from?: string;
      date_to?: string;
      sort_order?: 'asc' | 'desc';
      page?: number;
      page_size?: number;
    } = {}
  ): Promise<PageResult<FundTransactionSummary>> {
    const dto = await apiClient.get<PageResult<ApiFundTransactionSummary>>(
      `/funds/${fundId}/fund-transactions${_qs({ ...params })}`
    );
    return { ...dto, items: dto.items.map(mapFundTransactionSummary) };
  },

  async detail(id: string): Promise<FundTransactionDetail> {
    const dto = await apiClient.get<ApiFundTransactionDetail>(
      `/fund-transactions/${id}`
    );
    return mapFundTransactionDetail(dto);
  },

  getReconciliation(id: string): Promise<ReconciliationStateResponse> {
    return apiClient.get(`/fund-transactions/${id}/reconciliation`);
  },

  getJournalEntries(id: string): Promise<FundTransactionGLTransaction[]> {
    return apiClient.get(`/fund-transactions/${id}/journal-entries`);
  },

  adminApprove(id: string): Promise<ReconciliationStateResponse> {
    return apiClient.post(`/fund-transactions/${id}/admin-approve`, {});
  },

  manualApprove(
    id: string,
    data: ManualApproveRequest
  ): Promise<ReconciliationStateResponse> {
    return apiClient.post(`/fund-transactions/${id}/manual-approve`, data);
  },

  async listBankEntries(
    fundId: string,
    id: string
  ): Promise<BankEntryResponse[]> {
    const dtos = await apiClient.get<ApiBankEntryResponse[]>(
      `/funds/${fundId}/fund-transactions/${id}/bank-entries`
    );
    return dtos.map(mapBankEntryResponse);
  },

  addBankEntry(
    fundId: string,
    id: string,
    data: AddBankEntryRequest
  ): Promise<BankEntryResponse> {
    return apiClient.post(
      `/funds/${fundId}/fund-transactions/${id}/bank-entries`,
      data
    );
  },

  updateBankEntry(
    fundId: string,
    id: string,
    entryId: string,
    data: UpdateBankEntryRequest
  ): Promise<BankEntryResponse> {
    return apiClient.put(
      `/funds/${fundId}/fund-transactions/${id}/bank-entries/${entryId}`,
      data
    );
  },

  deleteBankEntry(fundId: string, id: string, entryId: string): Promise<void> {
    return apiClient.delete(
      `/funds/${fundId}/fund-transactions/${id}/bank-entries/${entryId}`
    );
  },

  generateManagementFeeDrafts(
    fundId: string
  ): Promise<GenerateManagementFeeDraftsResponse> {
    return apiClient.post(
      `/funds/${fundId}/fund-transactions/management-fees/generate`,
      {}
    );
  },

  async listActivity(
    fundId: string,
    transactionId: string
  ): Promise<TransactionActivityEntry[]> {
    return apiClient.get<TransactionActivityEntry[]>(
      `/funds/${fundId}/fund-transactions/${transactionId}/activity`
    );
  },

  async listDocuments(
    fundId: string,
    transactionId: string
  ): Promise<FundTransactionDocument[]> {
    return apiClient.get<FundTransactionDocument[]>(
      `/funds/${fundId}/fund-transactions/${transactionId}/documents`
    );
  },

  async linkDocument(
    fundId: string,
    transactionId: string,
    data: FundTransactionDocumentLinkRequest
  ): Promise<FundTransactionDocumentCreateResponse> {
    return apiClient.post<FundTransactionDocumentCreateResponse>(
      `/funds/${fundId}/fund-transactions/${transactionId}/documents`,
      data
    );
  },

  async unlinkDocument(
    fundId: string,
    transactionId: string,
    linkId: string
  ): Promise<void> {
    return apiClient.delete<void>(
      `/funds/${fundId}/fund-transactions/${transactionId}/documents/${linkId}`
    );
  },
};
